import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, Check, ChevronRight, ChevronLeft, MapPin, Building,
  Hash, Map, Home, Plus, Loader2, Star, ChevronUp,
  Edit2, CheckCircle, AlertTriangle, Truck, Clock, ArrowRight
} from 'lucide-react';
import StepIndicator from './StepIndicator';
import { GeoapifyContext, GeoapifyGeocoderAutocomplete } from '@geoapify/react-geocoder-autocomplete';
import '@geoapify/geocoder-autocomplete/styles/minimal.css';
import { supabase } from '../../services/supabase';
import SingleMealSelection from './SingleMealSelection';

const DateSelectionStyles = () => (
  <style>{`
    :root {
      --input-height: 54px;
    }

    .date-section {
      max-width: 1200px;
      margin: 0 auto;
      background: #ffffff;
      padding: clamp(20px, 4vw, 40px);
      border-radius: 4px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    }

    /* Single mode: unified page container */
    .single-page-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 6px;
    }
    .date-section.single-mode {
      max-width: none;
      padding: 0;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      background: transparent;
      margin-bottom: 24px;
    }
    .date-section.single-mode .address-section {
      margin-bottom: 0;
      padding-bottom: 20px;
      border-bottom: 1px solid #eee;
    }

    .delivery-verify-prompt {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 48px 24px;
      color: #999;
    }
    .delivery-verify-prompt svg {
      color: #2F7D5E;
      margin-bottom: 16px;
    }
    .delivery-verify-title {
      font-size: 1.1rem;
      font-weight: 400;
      color: #333;
      margin-bottom: 8px;
    }
    .delivery-verify-desc {
      font-size: 0.9rem;
      color: #888;
      max-width: 320px;
      line-height: 1.5;
    }

    .delivery-notes-section {
      margin-top: 16px;
    }
    .delivery-notes-label {
      display: block;
      font-size: 0.85rem;
      font-weight: 400;
      color: #555;
      margin-bottom: 6px;
    }
    .delivery-notes-input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      font-size: 0.9rem;
      font-family: inherit;
      resize: vertical;
      box-sizing: border-box;
      transition: border-color 0.15s ease;
    }
    .delivery-notes-input:focus {
      outline: none;
      border-color: #2F7D5E;
    }
    .delivery-notes-input::placeholder {
      color: #bbb;
    }

    .delivery-coming-soon {
      text-align: center;
      padding: 40px 24px;
      margin-top: 8px;
    }
    .delivery-coming-soon-icon {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
    }
    .delivery-coming-soon-icon svg {
      color: #2F7D5E;
    }
    .delivery-coming-soon-badge {
      display: inline-block;
      background: linear-gradient(135deg, #2F7D5E, #3a9e74);
      color: #fff;
      font-size: 0.75rem;
      font-weight: 400;
      padding: 4px 14px;
      border-radius: 20px;
      letter-spacing: 1px;
      margin-bottom: 12px;
    }
    .delivery-coming-soon h3 {
      font-size: 1.2rem;
      font-weight: 400;
      color: #333;
      margin: 0 0 8px;
    }
    .delivery-coming-soon p {
      font-size: 0.9rem;
      color: #888;
      line-height: 1.6;
      max-width: 340px;
      margin: 0 auto 24px;
    }
    .weekly-promo-card {
      background: linear-gradient(135deg, #f0faf5 0%, #e8f5e9 100%);
      border: 1px solid #c8e6c9;
      border-radius: 4px;
      padding: 24px;
      max-width: 380px;
      margin: 0 auto;
      text-align: left;
    }
    .weekly-promo-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
    }
    .weekly-promo-header svg {
      color: #2F7D5E;
    }
    .weekly-promo-header span {
      font-size: 1rem;
      font-weight: 400;
      color: #2F7D5E;
    }
    .weekly-promo-features {
      list-style: none;
      padding: 0;
      margin: 0 0 16px;
    }
    .weekly-promo-features li {
      font-size: 0.85rem;
      color: #555;
      padding: 4px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .weekly-promo-features li svg {
      color: #2F7D5E;
      flex-shrink: 0;
    }
    .weekly-promo-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px;
      background: #2F7D5E;
      color: #fff;
      border: none;
      border-radius: 4px;
      font-size: 0.95rem;
      font-weight: 400;
      cursor: pointer;
      transition: background 0.2s;
    }
    .weekly-promo-btn:hover {
      background: #256b4e;
    }

    .address-section {
      margin-bottom: clamp(30px, 5vw, 40px);
      padding-bottom: clamp(20px, 4vw, 30px);
      border-bottom: 1px solid #f0f0f0;
    }

    .address-header {
      display: flex;
      align-items: center;
      gap: clamp(8px, 2vw, 12px);
      margin-bottom: clamp(15px, 3vw, 20px);
      font-size: clamp(1.25rem, 4vw, 1.6rem);
      font-weight: 400;
      color: #1a1a1a;
      letter-spacing: -0.5px;
    }

    /* --- 新版：選中地址橫條 (解決右邊留白) --- */
    .selected-address-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fdfdfd;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px 24px;
      transition: all 0.2s ease;
      gap: 20px;
    }

    .selected-address-bar:hover {
      border-color: #d0d0d0;
      box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    }

    .address-info-left {
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1;
    }

    .address-top-line {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .address-main-text {
      font-size: 1.15rem;
      font-weight: 400;
      color: #1a1a1a;
    }

    .address-sub-text {
      font-size: 0.95rem;
      color: #666;
    }

    .address-actions-right {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
    }

    /* --- 展開後的網格 --- */
    .saved-addresses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 15px;
      margin-bottom: 20px;
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .saved-address-card {
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #fff;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .saved-address-card:hover {
      border-color: #ccc;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }

    .saved-address-card.selected {
      border-color: #2F7D5E;
      background: #fafafa;
      box-shadow: 0 4px 12px rgba(47,125,94,0.1);
    }

    .addr-tag {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.75rem;
      font-weight: 400;
      background: #f0f0f0;
      padding: 4px 10px;
      border-radius: 4px;
      color: #555;
    }

    .default-badge {
      background: #fff9db !important;
      color: #d48806 !important;
      border: 1px solid #ffe066;
    }

    .action-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 18px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      background: #fff;
      color: #555;
      font-size: 0.9rem;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }

    .action-btn:hover {
      background: #f4f4f4;
      color: #1a1a1a;
      border-color: #ccc;
    }

    .action-btn-primary {
      background: #2F7D5E;
      color: #fff;
      border: 1px solid #2F7D5E;
    }
    .action-btn-primary:hover {
      background: #245F48;
      color: #fff;
      border-color: #245F48;
    }

    .expand-control-area {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 20px;
      border-bottom: 1px dashed #eee;
      padding-bottom: 15px;
    }

    /* --- 輸入框樣式 --- */
    .geoapify-geocoder-autocomplete {
      background: transparent;
      border: none;
      padding: 0;
      width: 100%;
      position: relative;
    }

    .geoapify-autocomplete-input,
    .regular-input {
      width: 100%;
      height: var(--input-height);
      padding: 0 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      font-size: 16px;
      font-family: inherit;
      transition: all 0.3s ease;
      background: #fff;
      box-sizing: border-box;
      line-height: normal;
      color: #1a1a1a;
    }

    .regular-input {
      padding-left: 44px;
    }

    .geoapify-autocomplete-input:focus,
    .regular-input:focus {
      outline: none;
      border-color: #2F7D5E;
      box-shadow: 0 0 0 3px rgba(47, 125, 94, 0.15);
    }

    .geoapify-close-button {
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      line-height: 1;
    }

    .geoapify-autocomplete-items {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      border: none;
      box-shadow: 0 10px 30px rgba(0,0,0,0.15);
      border-radius: 4px;
      margin-top: 8px;
      background: #fff;
      overflow: hidden;
      z-index: 100;
    }

    .geoapify-autocomplete-items .geoapify-autocomplete-item {
      padding: 12px 16px;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;
      text-align: left;
    }

    .geoapify-autocomplete-items .geoapify-autocomplete-item:hover,
    .geoapify-autocomplete-items .geoapify-autocomplete-item.active {
      background-color: #f8f8f8;
    }

    .input-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;
      margin-top: 15px;
    }

    .regular-input-wrapper {
      position: relative;
      width: 100%;
    }

    .input-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      pointer-events: none;
      width: 20px;
      height: 20px;
      z-index: 2;
    }

    .save-address-option {
      margin-top: 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 15px;
      background: #f9f9f9;
      border-radius: 4px;
      flex-wrap: wrap;
    }

    .checkbox-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .custom-checkbox {
      width: 20px;
      height: 20px;
      border: 1px solid #ccc;
      border-radius: 4px;
      cursor: pointer;
      appearance: none;
      transition: all 0.2s;
    }

    .custom-checkbox:checked {
      background: #2F7D5E;
      border-color: #2F7D5E;
    }

    .checkbox-label {
      font-size: 0.95rem;
      color: #333;
      cursor: pointer;
      user-select: none;
    }

    .tag-input {
      border: none;
      background: transparent;
      border-bottom: 1px solid #ccc;
      padding: 5px 0;
      font-size: 0.9rem;
      color: #2F7D5E;
      width: 150px;
      margin-left: 10px;
    }
    .tag-input:focus { outline: none; border-color: #2F7D5E; }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: clamp(20px, 4vw, 35px);
      padding-bottom: clamp(15px, 3vw, 20px);
      border-bottom: 1px solid #f0f0f0;
      width: 100%;
      flex-wrap: wrap;
      gap: 15px;
    }

    .section-title {
      font-size: clamp(1.25rem, 4vw, 1.6rem);
      font-weight: 400;
      color: #1a1a1a;
      display: flex;
      align-items: center;
      gap: clamp(8px, 2vw, 12px);
      letter-spacing: -0.5px;
      flex: 1;
      min-width: 200px;
    }

    .counter-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
    }

    .counter {
      font-size: clamp(0.875rem, 2vw, 1rem);
      font-weight: 400;
      color: #666;
      background: #f5f5f5;
      padding: clamp(6px, 1.5vw, 8px) clamp(15px, 3vw, 20px);
      border-radius: 20px;
      white-space: nowrap;
    }

    .helper-text {
      font-size: clamp(0.7rem, 1.8vw, 0.75rem);
      color: #ff6b6b;
      font-weight: 400;
    }

    .days-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: clamp(12px, 2vw, 20px);
      margin-bottom: clamp(30px, 5vw, 50px);
    }

    .day-card {
      position: relative;
      padding: clamp(20px, 4vw, 30px) clamp(15px, 3vw, 20px);
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #fff;
      box-sizing: border-box;
    }

    .day-card:hover:not(.day-card-disabled) {
      border-color: #2F7D5E;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(47,125,94,0.08);
    }

    .day-card:active { transform: scale(0.98); }

    .day-card-selected {
      background: #2F7D5E;
      color: #fff;
      border-color: #2F7D5E;
      box-shadow: 0 8px 25px rgba(47,125,94,0.2);
    }

    .day-card-disabled { opacity: 0.3; cursor: not-allowed; }

    .check-icon {
      position: absolute;
      top: clamp(8px, 2vw, 12px);
      right: clamp(8px, 2vw, 12px);
      width: clamp(20px, 4vw, 24px);
      height: clamp(20px, 4vw, 24px);
      background: #fff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #2F7D5E;
    }

    .day-emoji {
      margin-bottom: clamp(10px, 2vw, 15px);
      color: #2F7D5E;
      opacity: 0.6;
    }
    .day-card-selected .day-emoji { color: #fff; opacity: 1; }

    .day-weekday {
      font-size: clamp(0.7rem, 1.8vw, 0.75rem);
      text-transform: uppercase;
      letter-spacing: 1px;
      opacity: 0.6;
      margin-bottom: 8px;
      font-weight: 400;
    }
    .day-card-selected .day-weekday { opacity: 0.8; }

    .day-date {
      font-size: clamp(1rem, 2.5vw, 1.15rem);
      font-weight: 400;
    }

    .btn-container {
      display: flex;
      justify-content: center;
      gap: 15px;
      flex-wrap: wrap;
    }

    .btn {
      padding: clamp(14px, 3vw, 16px) clamp(30px, 5vw, 40px);
      border: none;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      letter-spacing: 0.3px;
      border-radius: 4px;
      min-width: 140px;
      flex: 1;
    }
    .btn:active { transform: scale(0.98); }

    .btn-primary { background: #2F7D5E; color: #fff; box-shadow: 0 4px 12px rgba(47,125,94,0.2); border: 1px solid #2F7D5E; }
    .btn-secondary { background: #fff; color: #2F7D5E; border: 1px solid #e0e0e0; }
    .btn-disabled { background: #e8e8e8; color: #aaa; cursor: not-allowed; box-shadow: none; }

    .spinner { animation: spin 1s linear infinite; }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* Zone Feedback Styles */
    .zone-feedback {
        margin-top: 15px;
        padding: 12px;
        border-radius: 4px;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 400;
        animation: fadeIn 0.3s ease;
    }
    .zone-success {
        background-color: #fff;
        border: 1px solid #2F7D5E;
        color: #2F7D5E;
    }
    .zone-error {
        background-color: #fff2f0;
        border: 1px solid #ffccc7;
        color: #cf1322;
    }
    .zone-loading {
        background-color: #e6f7ff;
        border: 1px solid #91d5ff;
        color: #096dd9;
    }

    .address-hint {
        margin-top: 10px;
        padding: 10px 12px;
        border-radius: 4px;
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 400;
        background-color: #e6f7ff;
        border: 1px solid #91d5ff;
        color: #096dd9;
        animation: fadeIn 0.3s ease;
    }

    .input-locked {
        background: #f5f5f5 !important;
        color: #888 !important;
        cursor: default;
    }

    @keyframes fadeIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }

    /* Order Type Toggle for Single Mode */
    .order-type-section {
      margin-bottom: clamp(25px, 4vw, 35px);
      padding-bottom: clamp(20px, 4vw, 30px);
      border-bottom: 1px solid #f0f0f0;
    }
    .order-type-header {
      display: flex;
      align-items: center;
      gap: clamp(8px, 2vw, 12px);
      margin-bottom: clamp(15px, 3vw, 20px);
      font-size: clamp(1.25rem, 4vw, 1.6rem);
      font-weight: 400;
      color: #1a1a1a;
      letter-spacing: -0.5px;
    }
    .order-type-toggle {
      display: flex;
      gap: 0;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 4px;
      max-width: 400px;
    }
    .order-type-btn {
      flex: 1;
      padding: 10px 24px;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.2s ease;
      background: transparent;
      color: #666;
      font-family: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .order-type-btn.active {
      background: #2F7D5E;
      color: #fff;
      box-shadow: 0 2px 8px rgba(47,125,94,0.2);
    }
    .order-type-btn:not(.active):hover {
      background: #f5f5f5;
      color: #333;
    }

    @media (max-width: 768px) {
      .selected-address-bar { flex-direction: column; align-items: flex-start; gap: 15px; padding: 16px; }
      .address-top-line { flex-wrap: wrap; }
      .address-main-text { font-size: 1rem; }
      .address-sub-text { font-size: 0.85rem; }
      .address-actions-right { width: 100%; justify-content: space-between; }
      .action-btn { flex: 1; justify-content: center; }
      .section-header { flex-direction: column; align-items: flex-start; }
      .counter-wrapper { align-items: center; width: 100%; }
      .btn-container { flex-direction: column; }
      .btn { width: 100%; min-width: auto; }
      .input-grid { grid-template-columns: 1fr; }
    }
  `}</style>
);

const DateSelection = () => {
  const navigate = useNavigate();
  const contextData = useOutletContext();
  const { t } = useTranslation('booking');
  const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

  const {
    next7Days, selectedDays, toggleDay, MIN_DAYS, MAX_DAYS, bookingMode,
    deliveryAddress: ctxAddress, setDeliveryAddress: setCtxAddress,
    unitNumber: ctxUnit, setUnitNumber: setCtxUnit,
    city: ctxCity, setCity: setCtxCity,
    postalCode: ctxPostal, setPostalCode: setCtxPostal,
    province: ctxProvince, setProvince: setCtxProvince,
    user,
    // 🆕 區域偵測相關
    allZones,
    detectZone,
    matchedZone,
    setMatchedZone,
    // Single mode
    orderType, setOrderType,
    deliveryNotes, setDeliveryNotes
  } = contextData || {};

  const [localSavedAddresses, setLocalSavedAddresses] = useState([]);
  const [, setLoadingAddresses] = useState(false);
  const [isAddressesExpanded, setIsAddressesExpanded] = useState(false);

  const [addressMode, setAddressMode] = useState('new');
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // 本地地址 State
  const [address, setAddress] = useState(ctxAddress || '');
  const [unit, setUnit] = useState(ctxUnit || '');
  const [city, setCity] = useState(ctxCity || '');
  const [postal, setPostal] = useState(ctxPostal || '');
  const [province, setProvince] = useState(ctxProvince || 'BC');

  // 🆕 經緯度 State
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  // 🆕 地址是否已從下拉選取確認
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  const [isCheckingZone, setIsCheckingZone] = useState(false);
  const [zoneCheckError, setZoneCheckError] = useState('');
  const zoneCheckRequestIdRef = useRef(0);
  const [saveForFuture, setSaveForFuture] = useState(true);
  const [addressTag, setAddressTag] = useState('');

  useEffect(() => {
    if (!detectZone) return;
    if (lat == null || lng == null) return;
    if (!allZones || allZones.length === 0) return;

    const latNum = typeof lat === 'string' ? Number(lat) : lat;
    const lngNum = typeof lng === 'string' ? Number(lng) : lng;

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
      if (setMatchedZone) setMatchedZone(null);
      return;
    }

    detectZone(latNum, lngNum);
    setIsCheckingZone(false);
  }, [lat, lng, detectZone, allZones, setMatchedZone]);

  const geocodeToLatLng = async ({ addressText }) => {
    if (!addressText) return null;

    const url = `/api/geocode?text=${encodeURIComponent(addressText)}`;

    const res = await fetch(url);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Geocode failed: ${res.status} ${text}`.trim());
    }

    const json = await res.json();
    const best = json?.results?.[0];
    const nextLat = best?.lat;
    const nextLng = best?.lon;

    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return null;
    return { lat: nextLat, lng: nextLng };
  };

  const updateContext = (addr, unt, cty, pst, prv) => {
    if (setCtxAddress) setCtxAddress(addr);
    if (setCtxUnit) setCtxUnit(unt);
    if (setCtxCity) setCtxCity(cty);
    if (setCtxPostal) setCtxPostal(pst);
    if (setCtxProvince) setCtxProvince(prv);
  };

  // ✅ 處理選擇常用地址
  const handleSelectSaved = (savedAddr) => {

    setSelectedSavedId(savedAddr.id);
    setAddressConfirmed(true);
    setAddress(savedAddr.address);
    setUnit(savedAddr.unit);
    setCity(savedAddr.city);
    setPostal(savedAddr.postal);
    setProvince(savedAddr.province);

    // 🆕 檢查該地址是否有經緯度並偵測 Zone
    if (savedAddr.lat != null && savedAddr.lng != null) {
        zoneCheckRequestIdRef.current += 1;
        setIsCheckingZone(false);
        setZoneCheckError('');
        const nextLat = typeof savedAddr.lat === 'string' ? Number(savedAddr.lat) : savedAddr.lat;
        const nextLng = typeof savedAddr.lng === 'string' ? Number(savedAddr.lng) : savedAddr.lng;

        if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) {
          if (setMatchedZone) setMatchedZone(null);
        } else {
          if (setMatchedZone) setMatchedZone(null);
          setLat(nextLat);
          setLng(nextLng);
          if (detectZone) detectZone(nextLat, nextLng);
        }
    } else {
        const requestId = (zoneCheckRequestIdRef.current += 1);
        if (setMatchedZone) setMatchedZone(null);
        setLat(null);
        setLng(null);
        setZoneCheckError('');

        const addressText = `${savedAddr.address || ''}, ${savedAddr.city || ''}, ${savedAddr.province || ''} ${savedAddr.postal || ''}, Canada`
          .replace(/\s+/g, ' ')
          .trim();

        if (!addressText) return;

        setIsCheckingZone(true);
        geocodeToLatLng({ addressText })
          .then(async (coords) => {
            if (requestId !== zoneCheckRequestIdRef.current) return;
            if (!coords) {
              setZoneCheckError(t('date_selection.zone_geocode_error'));
              if (setMatchedZone) setMatchedZone(null);
              return;
            }

            setLat(coords.lat);
            setLng(coords.lng);

            if (detectZone) detectZone(coords.lat, coords.lng);

            if (user?.id && savedAddr.id) {
              try {
                await supabase
                  .from('saved_addresses')
                  .update({ lat: coords.lat, lng: coords.lng })
                  .eq('id', savedAddr.id)
                  .eq('user_id', user.id);
              } catch (e) {
                // silently ignore
              }
            }
          })
          .catch((e) => {
            if (requestId !== zoneCheckRequestIdRef.current) return;
            setZoneCheckError(t('date_selection.zone_geocode_fail'));
            if (setMatchedZone) setMatchedZone(null);
          })
          .finally(() => {
            if (requestId !== zoneCheckRequestIdRef.current) return;
            setIsCheckingZone(false);
          });
    }

    updateContext(savedAddr.address, savedAddr.unit, savedAddr.city, savedAddr.postal, savedAddr.province);
    setIsAddressesExpanded(false);

    // 將選中的地址設為 default
    if (user?.id && savedAddr.id) {
      setLocalSavedAddresses(prev => {
        const updated = prev.map(a => ({ ...a, is_default: a.id === savedAddr.id }));
        const selected = updated.find(a => a.id === savedAddr.id);
        const rest = updated.filter(a => a.id !== savedAddr.id);
        return [selected, ...rest];
      });
      // DB: 先清除所有 default，再設新的
      (async () => {
        try {
          const { error: e1 } = await supabase
            .from('saved_addresses')
            .update({ is_default: false })
            .eq('user_id', user.id)
            .neq('id', savedAddr.id);
          // e1 error silently ignored

          const { error: e2 } = await supabase
            .from('saved_addresses')
            .update({ is_default: true })
            .eq('id', savedAddr.id)
            .eq('user_id', user.id);
          // e2 error silently ignored
        } catch (err) {
          // silently ignore
        }
      })();
    }
  };

  const handleModeChange = (mode) => {
    setAddressMode(mode);
    if (mode === 'new') {
      zoneCheckRequestIdRef.current += 1;
      setIsCheckingZone(false);
      setZoneCheckError('');
      setSelectedSavedId(null);
      setAddressConfirmed(false);
      setAddress('');
      setUnit('');
      setCity('');
      setPostal('');
      setProvince('BC');

      // 清空經緯度與 Zone
      setLat(null);
      setLng(null);
      if (setMatchedZone) setMatchedZone(null);

      updateContext('', '', '', '', 'BC');
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAddresses = async () => {
      if (!user) return;

      setLoadingAddresses(true);

      try {
        const { data, error } = await supabase
          .from('saved_addresses')
          .select('*')
          .eq('user_id', user.id)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (!isMounted) return;

        if (data && data.length > 0) {
          setLocalSavedAddresses(data);

          if (!selectedSavedId && !ctxAddress) {
            setAddressMode('saved');
            handleSelectSaved(data[0]);
          } else if (ctxAddress) {
            setAddressMode('saved');
          }
        } else {
          setAddressMode('new');
        }
      } catch (error) {
        if (isMounted) setAddressMode('new');
      } finally {
        if (isMounted) setLoadingAddresses(false);
      }
    };

    fetchAddresses();

    return () => { isMounted = false; };
  }, [user]);

  // ✅ 處理 Geoapify 地址選擇
  const handlePlaceSelect = (value) => {
    if (!value) return;
    const props = value.properties;

    setAddressConfirmed(true);
    setAddress(props.address_line1 || props.formatted);
    setCity(props.city || '');
    setPostal(props.postcode || '');
    setProvince(props.state_code || props.state || 'BC');

    // 🆕 獲取經緯度並偵測 Zone
    const newLat = props.lat;
    const newLng = props.lon;
    setLat(newLat);
    setLng(newLng);

    if (detectZone) {
        detectZone(newLat, newLng);
    }

    updateContext(props.address_line1 || props.formatted, unit, props.city || '', props.postcode || '', props.state_code || props.state || 'BC');
  };

  const handleNext = async () => {
    // Single mode: pickup 不需驗證地址
    if (bookingMode === 'single' && orderType === 'pickup') {
      navigate('meals');
      return;
    }

    if (addressMode === 'new' && (!address || !city || !postal)) {
      alert(t('date_selection.alert_address'));
      return;
    }
    if (isCheckingZone) {
      alert(t('date_selection.alert_checking'));
      return;
    }

    // 如果是新地址且尚未從下拉選項選擇地址，提示用戶
    if (addressMode === 'new' && lat == null && lng == null) {
      alert(t('date_selection.alert_select_suggestion'));
      return;
    }

    if (bookingMode !== 'single' && selectedDays.length < MIN_DAYS) {
      alert(t('date_selection.alert_min_days', { min: MIN_DAYS }));
      return;
    }

    // 阻擋：如果不在配送區域，不允許下一步
    if (!matchedZone) {
      alert(t('date_selection.alert_out_of_range'));
      return;
    }

    if (addressMode === 'new' && saveForFuture && user) {
      setIsSaving(true);
      try {
        // 🆕 儲存時加入 lat, lng
        await supabase.from('saved_addresses').insert({
            user_id: user.id,
            address,
            unit: unit || '',
            city,
            postal,
            province,
            tag: addressTag || t('date_selection.default_tag'),
            lat: lat,
            lng: lng
        });
      } catch (err) { /* silently ignore */ }
      finally { setIsSaving(false); }
    }
    navigate('meals');
  };

  // 將 DB 中儲存的中文 tag 翻譯為當前語言
  const knownTagMap = { '常用': 'default_tag', '家': 'tag_home', '公司': 'tag_office', 'Default': 'default_tag', 'Home': 'tag_home', 'Office': 'tag_office' };
  const displayTag = (tag) => {
    const key = knownTagMap[tag];
    return key ? t(`date_selection.${key}`) : tag;
  };

  const isHomeTag = (tag) => tag === '家' || tag === 'Home';

  const isReady = bookingMode === 'single'
    ? (orderType === 'pickup')
    : (selectedDays.length >= MIN_DAYS && address && city && postal);
  const currentSelectedAddr = localSavedAddresses.find(a => a.id === selectedSavedId) || localSavedAddresses[0];

  return (
    <>
      <DateSelectionStyles />
      <StepIndicator currentStep={1} bookingMode={bookingMode} />

      <div className={bookingMode === 'single' ? 'single-page-container' : ''}>
      <div className={`date-section ${bookingMode === 'single' ? 'single-mode' : ''}`}>

        {/* Single mode: 自取/送餐 toggle */}
        {bookingMode === 'single' && (
          <div className="order-type-section">
            <div className="order-type-toggle">
              <button
                className={`order-type-btn ${orderType === 'pickup' ? 'active' : ''}`}
                onClick={() => setOrderType('pickup')}
              >
                {t('date_selection.pickup_label', '自取')}
              </button>
              <button
                className={`order-type-btn ${orderType === 'delivery' ? 'active' : ''}`}
                onClick={() => setOrderType('delivery')}
              >
                {t('date_selection.delivery_label', '送餐')}
              </button>
            </div>
          </div>
        )}

        {/* 單次送餐 - 即將上線提示 + 週餐推廣 */}
        {bookingMode === 'single' && orderType === 'delivery' && (
          <div className="delivery-coming-soon">
            <div className="delivery-coming-soon-icon">
              <Truck size={32} strokeWidth={1.5} />
            </div>
            <div className="delivery-coming-soon-badge">COMING SOON</div>
            <h3>單次送餐服務即將上線</h3>
            <p>我們正在努力準備中，敬請期待！<br/>目前可先體驗我們的週餐配送服務</p>

            <div className="weekly-promo-card">
              <div className="weekly-promo-header">
                <CalendarDays size={20} strokeWidth={2} />
                <span>週餐配送服務</span>
              </div>
              <ul className="weekly-promo-features">
                <li><Check size={16} strokeWidth={2.5} /> 超過百款菜式任你挑選，天天不重覆</li>
                <li><Check size={16} strokeWidth={2.5} /> 自選配送日，每天自動送到辦公室</li>
                <li><Check size={16} strokeWidth={2.5} /> 一次訂餐，整週免煩惱</li>
                <li><Clock size={16} strokeWidth={2.5} /> 準時送達，安坐等收餐就好</li>
              </ul>
              <button
                className="weekly-promo-btn"
                onClick={() => navigate('/booking/weekly')}
              >
                前往週餐配送 <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* 地址區塊 - single mode 自取時隱藏, single delivery 時也隱藏(即將上線) */}
        {bookingMode !== 'single' && (
        <div className="address-section">
          <h3 className="address-header">
            <MapPin />
            {t('date_selection.delivery_address')}
          </h3>

          {/* 模式 A: 選擇已存地址 */}
          {addressMode === 'saved' && localSavedAddresses.length > 0 && (
            <div className="saved-addresses-container">

              {isAddressesExpanded && (
                <>
                  <div className="expand-control-area">
                      <button className="action-btn" onClick={() => setIsAddressesExpanded(false)}>
                        {t('date_selection.collapse')} <ChevronUp size={16} />
                      </button>
                  </div>
                  <div className="saved-addresses-grid">
                    {localSavedAddresses.map((addr) => {
                      const isSelected = selectedSavedId === addr.id;
                      const isDefault = addr.is_default;
                      return (
                        <div
                          key={addr.id}
                          className={`saved-address-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSelectSaved(addr)}
                        >
                          <div style={{display:'flex', justifyContent:'space-between'}}>
                             <div className="addr-tag-group">
                                {isDefault && <span className="addr-tag default-badge"><Star size={10} fill="currentColor"/> {t('date_selection.default_badge')}</span>}
                                <span className="addr-tag">{displayTag(addr.tag)}</span>
                             </div>
                             {isSelected && <Check size={16} color="#2F7D5E" />}
                          </div>
                          <div style={{fontWeight:600}}>{addr.address} {addr.unit && `#${addr.unit}`}</div>
                          <div style={{fontSize:'0.9rem', color:'#666'}}>{addr.city}, {addr.postal}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {!isAddressesExpanded && currentSelectedAddr && (
                <div className="selected-address-bar">
                  <div className="address-info-left">
                      <div className="addr-tag" style={{background:'#2F7D5E', color:'#fff', alignSelf:'flex-start'}}>
                        {isHomeTag(currentSelectedAddr.tag) ? <Home size={12}/> : <Building size={12}/>}
                        {displayTag(currentSelectedAddr.tag)}
                      </div>
                      <span className="address-main-text">
                         {currentSelectedAddr.address} {currentSelectedAddr.unit && `#${currentSelectedAddr.unit}`}
                      </span>
                      <span className="address-sub-text">
                        {currentSelectedAddr.city}, {currentSelectedAddr.province} {currentSelectedAddr.postal}
                      </span>
                  </div>

                  <div className="address-actions-right">
                      {localSavedAddresses.length > 1 && (
                        <button className="action-btn" onClick={() => setIsAddressesExpanded(true)}>
                          <Edit2 size={14} /> {t('date_selection.switch_address')}
                        </button>
                      )}
                      <button className="action-btn action-btn-primary" onClick={() => handleModeChange('new')}>
                        <Plus size={16} /> {t('date_selection.use_new')}
                      </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 模式 B: 輸入新地址 */}
          {addressMode === 'new' && (
            <div className="new-address-form">
              {localSavedAddresses.length > 0 && (
                 <button
                   onClick={() => handleModeChange('saved')}
                   style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 15, background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500 }}
                 >
                   <ChevronLeft size={18} /> {t('date_selection.back_to_addresses')}
                 </button>
              )}

              <div style={{ marginBottom: '15px' }}>
                <GeoapifyContext apiKey={GEOAPIFY_API_KEY}>
                  <GeoapifyGeocoderAutocomplete
                    placeholder={t('date_selection.address_placeholder')}
                    countryCodes={['ca']}
                    value={address}
                    placeSelect={handlePlaceSelect}
                    preprocessHook={(value) => {
                      setAddress(value);
                      setAddressConfirmed(false);
                      setLat(null);
                      setLng(null);
                      if (setMatchedZone) setMatchedZone(null);
                      return value;
                    }}
                    suggestionsChange={(val) => {
                      if(!val) {
                          setAddress('');
                          setAddressConfirmed(false);
                          setLat(null);
                          setLng(null);
                          setZoneCheckError('');
                          if(setMatchedZone) setMatchedZone(null);
                          updateContext('', unit, city, postal, province);
                      }
                    }}
                  />
                </GeoapifyContext>
                {address && !addressConfirmed && (
                  <div className="address-hint">
                    <MapPin size={16} />
                    <span>{t('date_selection.address_hint_select')}</span>
                  </div>
                )}
              </div>

              <div className="input-grid">
                <div className="regular-input-wrapper">
                  <Building className="input-icon" />
                  <input type="text" className="regular-input" placeholder={t('date_selection.unit_placeholder')} value={unit} onChange={(e) => { setUnit(e.target.value); updateContext(address, e.target.value, city, postal, province); }} />
                </div>
                <div className="regular-input-wrapper">
                  <MapPin className="input-icon" />
                  <input type="text" className={`regular-input${addressConfirmed ? ' input-locked' : ''}`} placeholder={t('date_selection.city_placeholder')} value={city} readOnly={addressConfirmed} onChange={(e) => { setCity(e.target.value); updateContext(address, unit, e.target.value, postal, province); }} />
                </div>
                <div className="regular-input-wrapper">
                  <Hash className="input-icon" />
                  <input type="text" className={`regular-input${addressConfirmed ? ' input-locked' : ''}`} placeholder={t('date_selection.postal_placeholder')} value={postal} readOnly={addressConfirmed} onChange={(e) => { setPostal(e.target.value.toUpperCase()); updateContext(address, unit, city, e.target.value.toUpperCase(), province); }} maxLength={7} />
                </div>
                <div className="regular-input-wrapper">
                  <Map className="input-icon" />
                  <input type="text" className={`regular-input${addressConfirmed ? ' input-locked' : ''}`} placeholder={t('date_selection.province_placeholder')} value={province} readOnly={addressConfirmed} onChange={(e) => { setProvince(e.target.value); updateContext(address, unit, city, postal, e.target.value); }} />
                </div>
              </div>

              {user && (
                <div className="save-address-option">
                  <label className="checkbox-wrapper">
                    <input type="checkbox" className="custom-checkbox" checked={saveForFuture} onChange={(e) => setSaveForFuture(e.target.checked)} />
                    <span className="checkbox-label" style={{ marginLeft: '10px' }}>{t('date_selection.save_address')}</span>
                  </label>
                  {saveForFuture && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', color: '#666' }}>{t('date_selection.tag_label')}</span>
                      <input type="text" className="tag-input" placeholder={t('date_selection.tag_placeholder')} value={addressTag} onChange={(e) => setAddressTag(e.target.value)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 🆕 區域反饋 UI */}
          {addressConfirmed && address && city && (
            <div className={`zone-feedback ${isCheckingZone ? 'zone-loading' : matchedZone ? 'zone-success' : 'zone-error'}`}>
                {isCheckingZone ? (
                    <>
                        <Loader2 size={18} className="spinner" />
                        <span>{t('date_selection.zone_checking')}</span>
                    </>
                ) : matchedZone ? (
                    <>
                        <CheckCircle size={18}/>
                        <span>{t('date_selection.zone_success', { zone: matchedZone.name })}</span>
                    </>
                ) : (
                    <>
                        <AlertTriangle size={18}/>
                        <span>{zoneCheckError || t('date_selection.zone_error_default')}</span>
                    </>
                )}
            </div>
          )}

          {/* 配送補充資訊 */}
          {matchedZone && (
            <div className="delivery-notes-section">
              <label className="delivery-notes-label">{t('date_selection.delivery_notes_label', '配送備註（選填）')}</label>
              <textarea
                className="delivery-notes-input"
                placeholder={t('date_selection.delivery_notes_placeholder', '例如：大門密碼、樓層、放門口等')}
                value={deliveryNotes || ''}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                rows={2}
              />
            </div>
          )}

        </div>
        )}

        {/* 日期選擇 - single mode 時隱藏 */}
        {bookingMode !== 'single' && (
        <>
        <div className="section-header">
            <h2 className="section-title"><CalendarDays /> {t('date_selection.select_dates')}</h2>
            <div className="counter-wrapper">
            <div className="counter">{selectedDays.length} / {MAX_DAYS}</div>
            {selectedDays.length < MIN_DAYS && <div className="helper-text">{t('date_selection.min_days_hint', { min: MIN_DAYS })}</div>}
            </div>
        </div>
        <div className="days-grid">
            {next7Days.map(day => {
            const isSelected = selectedDays.includes(day.id);
            const isDisabled = !isSelected && selectedDays.length >= MAX_DAYS && bookingMode !== 'single';
            return (
                <div key={day.id} onClick={() => toggleDay(day.id)} className={`day-card ${isSelected ? 'day-card-selected' : ''} ${isDisabled ? 'day-card-disabled' : ''}`}>
                {isSelected && <div className="check-icon"><Check size={16} /></div>}
                <div className="day-emoji"><CalendarDays size={28} strokeWidth={1.5} /></div>
                <div className="day-weekday">{day.weekday}</div>
                <div className="day-date">{day.fullDate}</div>
                </div>
            );
            })}
        </div>
        </>
        )}

        {/* 底部按鈕 - 只在 weekly 模式顯示 */}
        {bookingMode !== 'single' && (
          <div className="btn-container">
            <button onClick={() => navigate('/')} className="btn btn-secondary" disabled={isSaving}><ChevronLeft size={18} /> {t('date_selection.back_home')}</button>

            {/* 按鈕文字根據狀態變化 */}
            <button
              onClick={handleNext}
              disabled={!isReady || isSaving || isCheckingZone}
              className={`btn btn-primary ${(!isReady || isSaving || isCheckingZone) ? 'btn-disabled' : ''}`}
            >
              {isSaving ? (
                  <><Loader2 size={18} className="spinner" /><span>{t('date_selection.processing')}</span></>
              ) : isCheckingZone ? (
                  <>{t('date_selection.checking_zone')}</>
              ) : (
                  <>{t('date_selection.next_meals')} <ChevronRight size={18} /></>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Single mode: 直接顯示菜單與購物車 */}
      {bookingMode === 'single' && (orderType === 'pickup' || matchedZone) && (
        <SingleMealSelection embedded />
      )}
      </div>{/* close single-page-container or empty div */}
    </>
  );
};

export default DateSelection;
